import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, X, ChevronLeft, ChevronRight, Maximize2, ZoomIn, Images } from 'lucide-react';
import { fetchReferenceBySlug, getItemImages, optimizeImageUrl } from '../lib/cms';
import { siteConfig } from '../data';

export default function ProjectDetail() {
  const { slug } = useParams();
  
  // Initial fallback to siteConfig if present
  const initialProject = siteConfig.references?.find(p => p.slug === slug) || null;
  const [project, setProject] = useState(initialProject);
  const [loading, setLoading] = useState(!initialProject);
  const [notFound, setNotFound] = useState(false);

  // Modal lightbox state
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    let isMounted = true;
    async function loadProject() {
      setLoading(true);
      setNotFound(false);
      const data = await fetchReferenceBySlug(slug);
      if (isMounted) {
        if (data) {
          setProject(data);
          setNotFound(false);
        } else if (!initialProject) {
          setNotFound(true);
        }
        setLoading(false);
      }
    }
    loadProject();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  const images = project ? getItemImages(project) : [];

  // Modal navigation handlers
  const openModal = (index) => {
    setActiveImageIndex(index);
  };

  const closeModal = useCallback(() => {
    setActiveImageIndex(null);
  }, []);

  const nextImage = useCallback(() => {
    if (images.length === 0) return;
    setActiveImageIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    if (images.length === 0) return;
    setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Keyboard navigation and body scroll lock
  useEffect(() => {
    if (activeImageIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [activeImageIndex, closeModal, nextImage, prevImage]);

  if (loading && !project) {
    return (
      <div className="pt-8 md:pt-12 pb-24 animate-in fade-in duration-500">
        <div className="max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 text-slate-400 mb-12">
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Tilbage til referencer</span>
          </div>
          <div className="max-w-3xl animate-pulse space-y-6">
            <div className="h-4 bg-slate-100 w-24" />
            <div className="h-10 bg-slate-100 w-3/4" />
            <div className="h-20 bg-slate-100 w-full" />
            <div className="aspect-[16/10] bg-slate-100 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !project) {
    return (
      <div className="pt-16 pb-24 text-center">
        <div className="max-w-md mx-auto px-6">
          <h1 className="text-2xl font-serif text-slate-900 mb-4">Projektet blev ikke fundet</h1>
          <p className="text-slate-500 mb-8 font-light">
            Den reference du leder efter findes desværre ikke eller er blevet flyttet.
          </p>
          <Link
            to="/referencer"
            className="inline-flex items-center gap-2 bg-[#9abc5a] text-white px-6 py-3 font-medium hover:bg-[#89a84d] transition-colors"
          >
            <ArrowLeft size={16} />
            <span>Se alle referencer</span>
          </Link>
        </div>
      </div>
    );
  }

  const fullDesc = project.full_description || project.fullDescription || project.description || '';
  const isHtml = typeof fullDesc === 'string' && (fullDesc.includes('<p>') || fullDesc.includes('<br') || fullDesc.includes('<div>') || fullDesc.includes('<strong>'));

  return (
    <div className="pt-8 md:pt-12 pb-24 animate-in fade-in duration-700">
      <div className="max-w-5xl mx-auto px-6">
        <Link 
          to="/referencer" 
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-10 group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Tilbage til referencer</span>
        </Link>
        
        <div className="max-w-4xl">
          {project.category && (
            <span className="inline-block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-4 px-2.5 py-1 bg-slate-100 rounded-sm">
              {project.category}
            </span>
          )}

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif text-slate-900 mb-8 leading-tight">
            {project.title}
          </h1>

          {/* Project Details Grid (Client, Bygherre, Opgave) */}
          {(project.client || project.bygherre || project.opgave) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 p-6 bg-slate-50 border border-slate-100 rounded-sm mb-10 text-sm">
              {project.opgave && (
                <div className="sm:col-span-2 md:col-span-3">
                  <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Opgave</span>
                  <p className="text-slate-800 font-medium leading-relaxed">{project.opgave}</p>
                </div>
              )}
              {project.client && (
                <div>
                  <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Kunde</span>
                  <p className="text-slate-800 font-medium">{project.client}</p>
                </div>
              )}
              {project.bygherre && (
                <div>
                  <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Bygherre</span>
                  <p className="text-slate-800 font-medium">{project.bygherre}</p>
                </div>
              )}
            </div>
          )}

          {/* Project Description */}
          {fullDesc && (
            <div className="prose prose-slate max-w-none text-base md:text-lg text-slate-600 leading-relaxed mb-12">
              {isHtml ? (
                <div 
                  className="space-y-4 [&>p]:leading-relaxed [&_strong]:text-slate-900 [&_strong]:font-semibold"
                  dangerouslySetInnerHTML={{ __html: fullDesc }} 
                />
              ) : (
                <p className="whitespace-pre-line leading-relaxed">
                  {fullDesc}
                </p>
              )}
            </div>
          )}

          {/* Image Gallery Section */}
          {images.length > 0 && (
            <section className="mb-16 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Images size={20} className="text-[#9abc5a]" />
                  <h2 className="text-xl font-serif text-slate-900">
                    Billeder fra projektet
                  </h2>
                  <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full ml-1">
                    {images.length} {images.length === 1 ? 'billede' : 'billeder'}
                  </span>
                </div>
                <span className="text-xs text-slate-400 hidden sm:inline-block">
                  Klik på et billede for at åbne fuld skærm
                </span>
              </div>

              {images.length === 1 ? (
                /* Single Image View */
                <button
                  type="button"
                  onClick={() => openModal(0)}
                  className="group relative w-full overflow-hidden bg-slate-100 rounded-sm cursor-zoom-in block text-left focus:outline-none focus:ring-2 focus:ring-[#9abc5a]"
                  aria-label="Åbn billede i fuld skærm"
                >
                  <img 
                    src={optimizeImageUrl(images[0], 'width=1600,quality=85,format=auto')} 
                    alt={`${project.title} - Billede 1`}
                    className="w-full h-auto max-h-[700px] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-white/95 text-slate-900 px-4 py-2 rounded-sm text-sm font-medium flex items-center gap-2 shadow-lg transform translate-y-2 group-hover:translate-y-0">
                      <ZoomIn size={16} className="text-[#9abc5a]" />
                      <span>Se i fuld skærm</span>
                    </div>
                  </div>
                </button>
              ) : (
                /* Multi Image Gallery Grid */
                <div className="space-y-4">
                  {/* Primary Featured Image */}
                  <button
                    type="button"
                    onClick={() => openModal(0)}
                    className="group relative w-full overflow-hidden bg-slate-100 rounded-sm cursor-zoom-in block text-left focus:outline-none focus:ring-2 focus:ring-[#9abc5a]"
                    aria-label="Åbn hovedbillede i fuld skærm"
                  >
                    <div className="aspect-[16/10] sm:aspect-[16/9] overflow-hidden">
                      <img 
                        src={optimizeImageUrl(images[0], 'width=1600,quality=85,format=auto')} 
                        alt={`${project.title} - Billede 1`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                        loading="eager"
                      />
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-white/95 text-slate-900 px-4 py-2 rounded-sm text-sm font-medium flex items-center gap-2 shadow-lg transform translate-y-2 group-hover:translate-y-0">
                        <ZoomIn size={16} className="text-[#9abc5a]" />
                        <span>Se i fuld skærm (1 af {images.length})</span>
                      </div>
                    </div>
                  </button>

                  {/* Secondary Thumbnails / Grid */}
                  <div className={`grid gap-4 ${
                    images.length === 2 
                      ? 'grid-cols-1' 
                      : images.length === 3 
                        ? 'grid-cols-2' 
                        : 'grid-cols-2 sm:grid-cols-3'
                  }`}>
                    {images.slice(1).map((img, idx) => {
                      const imageIndex = idx + 1;
                      const thumbUrl = optimizeImageUrl(img, 'width=800,quality=80,format=auto');
                      return (
                        <button
                          key={imageIndex}
                          type="button"
                          onClick={() => openModal(imageIndex)}
                          className="group relative aspect-[4/3] overflow-hidden bg-slate-100 rounded-sm cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#9abc5a]"
                          aria-label={`Åbn billede ${imageIndex + 1} af ${images.length} i fuld skærm`}
                        >
                          <img 
                            src={thumbUrl} 
                            alt={`${project.title} - Billede ${imageIndex + 1}`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-all duration-300 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-white/95 text-slate-900 px-3 py-1.5 rounded-sm text-xs font-medium flex items-center gap-1.5 shadow-md">
                              <ZoomIn size={14} className="text-[#9abc5a]" />
                              <span>Billede {imageIndex + 1}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          )}
          
          {/* Back to overview link */}
          <div className="pt-12 border-t border-slate-100 flex items-center justify-between">
            <Link 
              to="/referencer" 
              className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-950 font-medium text-sm transition-colors border-b border-slate-300 pb-1 hover:border-slate-900"
            >
              <ArrowLeft size={16} />
              <span>Se alle referencer</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Lightbox Modal */}
      {activeImageIndex !== null && images.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col justify-between p-4 md:p-6 animate-in fade-in duration-200"
          role="dialog"
          aria-modal="true"
          aria-label="Billedvisning i fuld skærm"
        >
          {/* Modal Header Bar */}
          <div className="flex items-center justify-between text-white/90 pb-3 border-b border-white/10 z-10">
            <div className="flex items-center gap-3 truncate pr-4">
              <span className="font-serif text-base md:text-lg truncate font-medium">
                {project.title}
              </span>
              <span className="text-xs bg-white/10 text-white/80 px-2.5 py-1 rounded-full whitespace-nowrap">
                {activeImageIndex + 1} / {images.length}
              </span>
            </div>

            <button
              type="button"
              onClick={closeModal}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Luk billedvisning"
            >
              <X size={24} />
            </button>
          </div>

          {/* Modal Central Image View with Prev/Next Controls */}
          <div className="relative flex-1 flex items-center justify-center py-4 min-h-0 overflow-hidden">
            {images.length > 1 && (
              <button
                type="button"
                onClick={prevImage}
                className="absolute left-2 md:left-6 z-20 p-3 md:p-4 rounded-full bg-black/50 text-white/80 hover:text-white hover:bg-black/80 transition-all focus:outline-none focus:ring-2 focus:ring-white backdrop-blur-sm shadow-xl"
                aria-label="Forrige billede"
              >
                <ChevronLeft size={28} />
              </button>
            )}

            <div 
              className="max-h-full max-w-full flex items-center justify-center p-2"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={optimizeImageUrl(images[activeImageIndex], 'width=2000,quality=90,format=auto')}
                alt={`${project.title} - Billede ${activeImageIndex + 1}`}
                className="max-h-[65vh] sm:max-h-[72vh] md:max-h-[78vh] w-auto max-w-full object-contain select-none shadow-2xl rounded-sm transition-all duration-300"
              />
            </div>

            {images.length > 1 && (
              <button
                type="button"
                onClick={nextImage}
                className="absolute right-2 md:right-6 z-20 p-3 md:p-4 rounded-full bg-black/50 text-white/80 hover:text-white hover:bg-black/80 transition-all focus:outline-none focus:ring-2 focus:ring-white backdrop-blur-sm shadow-xl"
                aria-label="Næste billede"
              >
                <ChevronRight size={28} />
              </button>
            )}
          </div>

          {/* Modal Footer / Thumbnails Navigation */}
          <div className="pt-3 border-t border-white/10 z-10">
            {images.length > 1 && (
              <div className="flex items-center justify-center gap-2 md:gap-3 overflow-x-auto py-1 max-w-full">
                {images.map((thumb, idx) => {
                  const isActive = idx === activeImageIndex;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImageIndex(idx)}
                      className={`relative flex-shrink-0 w-14 h-14 md:w-20 md:h-20 rounded-sm overflow-hidden transition-all ${
                        isActive 
                          ? 'ring-2 ring-[#9abc5a] ring-offset-2 ring-offset-black opacity-100 scale-105' 
                          : 'opacity-50 hover:opacity-90'
                      }`}
                      aria-label={`Gå til billede ${idx + 1}`}
                    >
                      <img 
                        src={optimizeImageUrl(thumb, 'width=200,quality=70,format=auto')} 
                        alt={`Miniature ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            )}
            
            <p className="text-center text-xs text-white/50 mt-2">
              Brug piletasterne ← → til at navigere · Tryk Esc for at lukke
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
